import type { ApexOptions } from 'apexcharts';

/** Resolve CSS custom property from document (falls back if SSR). */
export function bhCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function bhChartPalette(): string[] {
  return [
    bhCssVar('--bh-chart-1', '#2563eb'),
    bhCssVar('--bh-chart-2', '#0284c7'),
    bhCssVar('--bh-chart-3', '#f59e0b'),
    bhCssVar('--bh-chart-4', '#ef4444'),
    bhCssVar('--bh-chart-5', '#64748b'),
    bhCssVar('--bh-chart-6', '#10b981'),
  ];
}

export function bhIsDarkTheme(): boolean {
  if (typeof document === 'undefined') return false;
  return (
    document.body.classList.contains('theme-dark') ||
    document.documentElement.getAttribute('data-theme') === 'dark' ||
    document.documentElement.classList.contains('dark')
  );
}

/** Shared Apex base options aligned to BizHub tokens. */
export function bhChartBaseOptions(overrides: ApexOptions = {}): ApexOptions {
  const isDark = bhIsDarkTheme();
  const muted = bhCssVar('--bh-chart-label', isDark ? '#94a3b8' : '#64748b');
  const grid = bhCssVar('--bh-chart-grid', isDark ? '#2a3544' : '#e2e8f0');
  const { chart: chartOverride, theme: themeOverride, ...rest } = overrides;

  return {
    ...rest,
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
      foreColor: muted,
      background: 'transparent',
      animations: { enabled: true, speed: 400 },
      ...chartOverride,
    },
    theme: { mode: isDark ? 'dark' : 'light', ...themeOverride },
    colors: overrides.colors || bhChartPalette(),
    dataLabels: { enabled: false, ...(overrides.dataLabels || {}) },
    stroke: { curve: 'smooth', width: 2.5, ...(overrides.stroke || {}) },
    grid: {
      borderColor: grid,
      strokeDashArray: 4,
      ...(overrides.grid || {}),
    },
    tooltip: { theme: isDark ? 'dark' : 'light', ...(overrides.tooltip || {}) },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      labels: { colors: muted },
      ...(overrides.legend || {}),
    },
  };
}
